import React, { useState, useEffect } from 'react';
import { 
  Building2, Users, MessageSquare, Bell, Settings2, Crown, 
  FileText, ChevronRight, ChevronLeft, Menu, ShoppingCart, LogOut
} from 'lucide-react';
import { getTickets } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import Loader from '../components/Loader';
import { AdminOrdersPanel } from '../components/admin/AdminOrdersPanel';
import { AdminClinicsPanel } from '../components/admin/AdminClinicsPanel';
import { AdminUsersPanel } from '../components/admin/AdminUsersPanel';
import { AdminPricingPanel } from '../components/admin/AdminPricingPanel';
import { AdminTicketPanel } from '../components/admin/AdminTicketPanel';
import { AdminNotificationsPanel } from '../components/admin/AdminNotificationsPanel';
import { AdminDemosPanel } from '../components/admin/AdminDemosPanel';
import { AdminIntegrationsPanel } from '../components/admin/AdminIntegrationsPanel';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_140853a5-d37e-4724-8f64-c2f7eabb8728/artifacts/4msp2ofk_agrosvetlogo.png';

const Admin = () => {
  const { logout } = useAuth();
  const [activeTab, setActiveTab] = useState('orders');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [openTicketCount, setOpenTicketCount] = useState(0);

  const navItems = [
    { key: 'orders', icon: ShoppingCart, label: 'Satışlar' },
    { key: 'clinics', icon: Building2, label: 'Klinikler' },
    { key: 'users', icon: Users, label: 'Kullanıcılar' },
    { key: 'pricing', icon: Crown, label: 'Fiyatlar' },
    { key: 'tickets', icon: MessageSquare, label: 'Destek' },
    { key: 'notif', icon: Bell, label: 'Bildirim' },
    { key: 'demos', icon: FileText, label: 'Demolar' },
    { key: 'integrations', icon: Settings2, label: 'Entegrasyon' },
  ];

  useEffect(() => {
    getTickets()
      .then(r => setOpenTicketCount((r.data || []).filter(t => t.status !== 'closed').length))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loader fullScreen />;

  return (
    <div className="min-h-screen bg-[#F2F2F7] flex">
      {/* Sidebar Overlay */}
      {sidebarOpen && <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Sidebar */}
      <aside className={`fixed lg:sticky top-0 left-0 h-screen bg-white border-r border-gray-200 z-50 transform transition-all ${sidebarOpen ? 'translate-x-0 w-64' : '-translate-x-full lg:translate-x-0 lg:w-16'} flex flex-col`}>
        <div className="p-4 border-b border-gray-100">
          {sidebarOpen
            ? <div className="flex items-center gap-2"><img src={LOGO_URL} alt="AGROSVET" className="w-8 h-8 object-contain" /><div><h1 className="text-lg font-bold text-gray-900">AGROSVET</h1><p className="text-xs text-[#8E8E93]">Yönetim Paneli</p></div></div>
            : <img src={LOGO_URL} alt="AGROSVET" className="w-8 h-8 object-contain mx-auto" />
          }
        </div>
        <nav className="flex-1 overflow-y-auto py-2">
          {navItems.map(item => {
            const badgeCount = item.key === 'tickets' ? openTicketCount : 0;
            return (
              <button key={item.key} onClick={() => { setActiveTab(item.key); if (window.innerWidth < 1024) setSidebarOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-all ${!sidebarOpen ? 'lg:justify-center lg:px-0' : ''} ${activeTab === item.key ? 'bg-[#6b6de3]/10 text-[#6b6de3] font-semibold border-r-2 border-[#6b6de3]' : 'text-gray-600 hover:bg-gray-50'}`}
                data-testid={`nav-${item.key}`} title={item.label}>
                <span className="relative">
                  <item.icon size={18} />
                  {badgeCount > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-[16px] flex items-center justify-center text-[9px] font-bold text-white bg-[#FF3B30] rounded-full px-0.5">{badgeCount}</span>
                  )}
                </span>
                {sidebarOpen && <span className="flex-1">{item.label}</span>}
                {sidebarOpen && badgeCount > 0 && (
                  <span className="min-w-[20px] h-[20px] flex items-center justify-center text-[10px] font-bold text-white bg-[#FF3B30] rounded-full px-1">{badgeCount}</span>
                )}
              </button>
            );
          })}
        </nav>
        <div className="border-t border-gray-100">
          <button onClick={() => setSidebarOpen(v => !v)} className={`w-full flex items-center gap-2 px-4 py-3 text-sm text-[#8E8E93] hover:bg-gray-50 transition-all ${!sidebarOpen ? 'justify-center px-0' : ''}`} title={sidebarOpen ? 'Daralt' : 'Genişlet'} data-testid="sidebar-toggle-btn">
            {sidebarOpen ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
            {sidebarOpen && 'Paneli Daralt'}
          </button>
          <button onClick={logout} className={`w-full flex items-center gap-2 text-sm text-[#FF3B30] font-medium px-4 py-3 hover:bg-red-50 transition-all ${!sidebarOpen ? 'justify-center px-0' : ''}`} data-testid="admin-logout-btn" title={'Çıkış Yap'}>
            <LogOut size={16} /> {sidebarOpen && 'Çıkış Yap'}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 min-w-0">
        {/* Top Bar */}
        <header className="sticky top-0 z-30 bg-white border-b border-gray-200 px-5 py-4 flex items-center gap-3">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 rounded-lg hover:bg-gray-100">
            <Menu size={20} />
          </button>
          <h2 className="text-lg font-bold text-gray-900">{navItems.find(n => n.key === activeTab)?.label}</h2>
        </header>

        <main className="p-4 pb-8">
          {activeTab === 'orders' && <AdminOrdersPanel />}
          {activeTab === 'clinics' && <AdminClinicsPanel />}
          {activeTab === 'users' && <AdminUsersPanel />}
          {activeTab === 'pricing' && <AdminPricingPanel />}
          {activeTab === 'tickets' && <AdminTicketPanel />}
          {activeTab === 'notif' && <AdminNotificationsPanel />}
          {activeTab === 'demos' && <AdminDemosPanel />}
          {activeTab === 'integrations' && <AdminIntegrationsPanel />}
        </main>
      </div>
    </div>
  );
};

export default Admin;
